/**
 * Formatage des dates, durées et nombres pour l'interface, en français.
 *
 * Module pur et testable : aucune de ces fonctions ne lit l'horloge sans qu'on
 * lui passe explicitement l'instant de référence.
 */

/** Marqueur affiché lorsqu'une donnée est absente. */
export const EMPTY_VALUE = '—';

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'medium',
});

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' });

/** Heure seule, pour les lignes de journal. */
const TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** Noms de mois en français, pour un formatage indépendant du fuseau. */
const MONTHS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

/** Convertit une chaîne ISO en `Date`, ou `null` si elle est inexploitable. */
function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Date et heure complètes, dans le fuseau du navigateur. */
export function formatDateTime(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? DATE_TIME_FORMAT.format(date) : EMPTY_VALUE;
}

/** Date seule, sans heure. */
export function formatDate(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? DATE_FORMAT.format(date) : EMPTY_VALUE;
}

/**
 * Heure seule (`HH:MM:SS`), dans le fuseau du navigateur.
 *
 * Le panneau de logs découpait auparavant la chaîne ISO à la main
 * (`at.slice(11, 19)`), ce qui affichait l'heure **UTC** : sur la même page, les
 * lignes de log accusaient deux heures de retard sur l'horodatage de démarrage,
 * lui correctement localisé.
 */
export function formatTime(iso: string | null | undefined): string {
  const date = toDate(iso);
  return date ? TIME_FORMAT.format(date) : EMPTY_VALUE;
}

/**
 * Étiquette courte d'un jour au format `YYYY-MM-DD`, telle que renvoyée par
 * les séries temporelles de l'API. La chaîne est découpée à la main plutôt que
 * parsée en `Date` : un `new Date('2026-08-10')` est interprété en UTC et
 * bascule d'un jour dans les fuseaux négatifs.
 */
export function formatDayLabel(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!match) return day;
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return day;
  return `${Number(match[3])} ${month}`;
}

/**
 * Durée lisible à partir d'un nombre de millisecondes.
 * Les paliers suivent ce qu'on veut lire sur un pipeline : millisecondes en
 * dessous de la seconde, secondes en dessous de la minute, puis minutes.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return EMPTY_VALUE;
  if (ms < 0) return EMPTY_VALUE;

  if (ms < 1000) return `${Math.round(ms)} ms`;

  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
    // Une décimale sous la minute : la différence entre 3 s et 3,4 s compte
    // quand on compare deux exécutions du même build.
    return `${totalSeconds.toFixed(1).replace('.', ',')} s`;
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds - totalMinutes * 60);
  if (totalMinutes < 60) {
    return seconds > 0 ? `${totalMinutes} min ${seconds} s` : `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  return minutes > 0 ? `${hours} h ${String(minutes).padStart(2, '0')}` : `${hours} h`;
}

/** Durée écoulée entre deux instants ISO. */
export function formatElapsed(
  fromIso: string | null | undefined,
  toIso: string | null | undefined,
): string {
  const from = toDate(fromIso);
  const to = toDate(toIso);
  if (!from || !to) return EMPTY_VALUE;
  return formatDuration(to.getTime() - from.getTime());
}

/**
 * Ancienneté exprimée relativement à maintenant : « il y a 3 min ».
 * `now` est injectable pour rendre la fonction testable.
 */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  const date = toDate(iso);
  if (!date) return EMPTY_VALUE;

  const deltaMs = now.getTime() - date.getTime();
  if (deltaMs < 0) return formatDateTime(iso);

  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `il y a ${days} j`;

  return formatDate(iso);
}

/** Nombre entier avec séparateurs de milliers. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  return NUMBER_FORMAT.format(value);
}

/** Pourcentage à partir d'un ratio entre 0 et 1. */
export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return EMPTY_VALUE;
  return `${(ratio * 100).toFixed(digits).replace('.', ',')} %`;
}

/** Taille en octets, exprimée dans l'unité la plus lisible. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return EMPTY_VALUE;
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(2).replace('.', ',')} Mo`;
}

/** Mémoire en mégaoctets. */
export function formatMegabytes(mb: number | null | undefined): string {
  if (mb === null || mb === undefined || Number.isNaN(mb)) return EMPTY_VALUE;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1).replace('.', ',')} Go`;
  return `${formatNumber(Math.round(mb))} Mo`;
}
